import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { GOOGLE_SERVICE_NAMES, useGoogleServiceConnection } from '@/hooks/useGoogleServiceConnection';
import { GoogleService, GoogleServiceResource, GoogleServiceStatus, MembershipRole } from '@/lib/types';
import { IntegrationCard } from './integration-card';
import { GOOGLE_SERVICE_LOGOS, GoogleServiceManageModal } from './google-service-manage-modal';

const DESCRIPTIONS: Record<GoogleService, string> = {
  google_analytics: 'Connect your Google Analytics account to check conversion tracking and its link to Google Ads.',
  tag_manager: 'Connect your Tag Manager account to check your Google Ads conversion tag is published.',
};

const RESOURCE_LABELS: Record<GoogleService, string> = {
  google_analytics: 'Properties',
  tag_manager: 'Containers',
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });

const resourceName = (resource: GoogleServiceResource) =>
  resource.public_id ? `${resource.display_name} (${resource.public_id})` : resource.display_name;

const reachedSummary = (status: GoogleServiceStatus) => {
  if (status.state === 'checking') return 'Checking...';
  if (status.resources.length === 0) return 'None found';
  return status.resources.map(resourceName).join(', ');
};

interface GoogleServiceCardProps {
  service: GoogleService;
  status: GoogleServiceStatus | undefined;
  role: MembershipRole | undefined;
}

/**
 * The organization's Google Analytics or Tag Manager connection on Settings → Integrations (#6879), on the same card
 * and flow as Google Ads: Connect runs Google's popup, Manage opens what the connection reaches (where it can be
 * refreshed), and Disconnect sits beside Manage. A connection Google no longer honours renders as not connected, as a
 * broken Google Ads token does; one that works but reaches nothing stays connected and says so.
 */
export const GoogleServiceCard = ({ service, status, role }: GoogleServiceCardProps) => {
  const connection = useGoogleServiceConnection(service);
  const [isManageOpen, setIsManageOpen] = useState(false);
  const connected = !!status && status.state !== 'not_connected' && !status.reconnect_required;
  const canReplace = role === MembershipRole.OWNER || role === MembershipRole.ADMIN;
  const canDisconnect = connected && (canReplace || status?.connected_by_you === true);

  const connectData =
    connected && status.connected_at
      ? {
          connectedDate: formatDate(status.connected_at),
          accountId: status.google_email ?? '—',
          apiKey: reachedSummary(status),
          serviceName: GOOGLE_SERVICE_NAMES[service],
          accountIdLabel: 'Google Account',
          accountNameLabel: RESOURCE_LABELS[service],
        }
      : undefined;

  return (
    <>
      <IntegrationCard
        title={GOOGLE_SERVICE_NAMES[service]}
        description={DESCRIPTIONS[service]}
        logo={GOOGLE_SERVICE_LOGOS[service]}
        isEnabled={connected}
        connectData={connectData}
        onToggle={() => (connected ? setIsManageOpen(true) : connection.connect())}
        actionLabel={!connected && connection.isAuthorizing ? 'Connecting...' : undefined}
        actionDisabled={!status || connection.isAuthorizing || connection.isDisconnecting}
        actions={
          canDisconnect ? (
            <Button
              size="sm"
              variant="destructive"
              onClick={connection.disconnect}
              disabled={connection.isDisconnecting}
            >
              {connection.isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          ) : undefined
        }
      />
      {connected && (
        <GoogleServiceManageModal
          service={service}
          status={status}
          isOpen={isManageOpen}
          onClose={() => setIsManageOpen(false)}
          canRefresh={canReplace}
        />
      )}
    </>
  );
};
